import type { ClientConfigDto } from './types';

/**
 * Boot-time client configuration fetched once from `/api/config/client`. This endpoint is
 * anonymous by design — the SPA needs it before the user signs in to decide whether to show
 * the Identity password form or redirect to an OIDC IdP (Okta/Entra).
 *
 * Note: `lib/appInsights.ts` fetches the same endpoint independently for telemetry bootstrap;
 * the duplicate request is cheap and keeps the two concerns decoupled.
 */
let _promise: Promise<ClientConfigDto> | null = null;

const fallback: ClientConfigDto = {
  appInsightsConnectionString: null,
  environment: 'unknown',
  authProvider: 'Identity',
  oidcAuthority: null,
  oidcClientId: null,
  oidcScope: null
};

/**
 * Fetch the client config once and cache the promise. Subsequent callers share the same
 * in-flight (or resolved) request. Falls back to Identity mode if the endpoint is unreachable
 * so the password form is still shown rather than a blank screen.
 */
export function getClientConfig(): Promise<ClientConfigDto> {
  if (_promise) return _promise;

  _promise = (async () => {
    try {
      const res = await fetch('/api/config/client', { credentials: 'include' });
      if (!res.ok) return fallback;
      const cfg = (await res.json()) as Partial<ClientConfigDto>;
      return {
        appInsightsConnectionString: cfg.appInsightsConnectionString ?? null,
        environment: cfg.environment ?? 'unknown',
        authProvider: cfg.authProvider ?? 'Identity',
        oidcAuthority: cfg.oidcAuthority ?? null,
        oidcClientId: cfg.oidcClientId ?? null,
        oidcScope: cfg.oidcScope ?? null
      } satisfies ClientConfigDto;
    } catch {
      return fallback;
    }
  })();

  return _promise;
}
